/**
 * Story 3.1 — De herinneringskaart *"Klopten deze uren?"* op "Mijn verlof".
 *
 * Aandacht zonder alarm (AC-2): wit vlak, grijze rand, één navy linkerrand.
 * Nergens rood — er is niets mis, er moet alleen iets bevestigd worden.
 *
 * Alle zichtbare teksten komen woord voor woord uit de microcopy-tabel van
 * `ux-verlof-correctie-kortverlof-2026-08-05.md` (UX-DR14); herformuleren is
 * een wijziging, geen detail.
 *
 * Dit component KENT DE UITSLUITING NIET (AC-17). Welke verlofregels om een
 * bevestiging vragen, bepaalt de dienstlaag — hier komt een lijst binnen en
 * elke regel daarin krijgt dezelfde twee knoppen. Er is dus geen tweede vorm
 * voor "hier hoef je niets te bevestigen", en er wordt nergens naar een
 * verlofsoort of naar een boekingstoestand gekeken (KE-9, KE-10).
 *
 * De peildatum komt als eigenschap binnen; het component leest nooit zelf de
 * klok (AD-25).
 */

import type { TeBevestigenRegel } from '@functieloonhuis/shared';
import { formatNlDagEnMaand, slotregelTekst } from './correctieTermijnCopy';

/** AC-3: de korte bevestiging na een geslaagde klik, letterlijk (UX-DR14). */
const BEVESTIGD_TEKST = 'Uren bevestigd.';

/** De navy linkerrand uit AC-2 — één accent, geen gekleurd vlak. */
const KAART_KLASSEN =
  'mb-6 rounded-lg border border-[#E2E8F0] border-l-[3px] border-l-[#2F5A7A] bg-white p-4';

const PRIMAIRE_KNOP =
  'px-3 py-1.5 rounded-md bg-[#2F5A7A] text-white text-xs font-medium ' +
  'focus-visible:ring-2 focus-visible:ring-[#2F5A7A] focus-visible:ring-offset-2';

const SECUNDAIRE_KNOP =
  'px-3 py-1.5 rounded-md border border-[#E2E8F0] text-[#2F5A7A] text-xs font-medium ' +
  'hover:bg-[#EEF2F6] focus-visible:ring-2 focus-visible:ring-[#2F5A7A] focus-visible:ring-offset-2';

/** "2 uur" / "8 uur" — het getal zoals de dienstlaag het levert. */
function urenTekst(hours: number): string {
  return `${hours} uur`;
}

export interface UrenBevestigenKaartProps {
  regels: TeBevestigenRegel[];
  /** Vandaag in Europe/Amsterdam; nooit hier zelf opgehaald (AD-25). */
  peildatum: string;
  onBevestig: (id: number) => void;
  onAanpassen: (id: number) => void;
  /** Aanvraag-ids waarvoor zojuist op "Ja, dat klopt" is geklikt (AC-3). */
  zojuistBevestigd?: number[];
  /** Foutmelding per aanvraag-id, getoond bij de regel zelf (AC-9, UX-DR15). */
  foutPerRegel?: Record<number, string>;
}

export function UrenBevestigenKaart({
  regels,
  peildatum,
  onBevestig,
  onAanpassen,
  zojuistBevestigd = [],
  foutPerRegel = {},
}: UrenBevestigenKaartProps) {
  // AC-11: niets tonen is de uitkomst. Geen lege kaart, geen kop zonder inhoud.
  if (regels.length === 0) return null;

  // AC-8: vallen alle termijnen op dezelfde dag, dan liegt één gedeelde
  // slotregel onderaan niet en is hij rustiger dan dezelfde zin per regel.
  // Verschillen ze, dan moet de zin wél per regel staan — anders zou één datum
  // gelden voor verlofdagen die een andere termijn hebben.
  const termijnen = new Set(regels.map((regel) => regel.correctieTermijnEinde));
  const gedeeldeTermijn = termijnen.size === 1 ? regels[0]!.correctieTermijnEinde : null;

  return (
    <section
      role="region"
      aria-label="Uren bevestigen"
      data-testid="uren-bevestigen-kaart"
      className={KAART_KLASSEN}
    >
      <h2 className="text-sm font-semibold text-[#1E293B]">Klopten deze uren?</h2>

      <ul className="mt-3 space-y-3">
        {regels.map((regel) => {
          const isBevestigd = zojuistBevestigd.includes(regel.id);
          const fout = foutPerRegel[regel.id];
          const foutId = fout ? `uren-bevestigen-fout-${regel.id}` : undefined;

          return (
            <li
              key={regel.id}
              data-testid={`uren-bevestigen-regel-${regel.id}`}
              className="flex flex-wrap items-center justify-between gap-2 border-t border-[#E2E8F0] pt-3 first:border-t-0 first:pt-0"
            >
              <div className="text-sm text-[#1E293B]">
                <span className="font-medium">{formatNlDagEnMaand(regel.endDate)}</span>
                {/* Zonder tijden geen leeg streepje: bij dagdeelverlof is er
                    domweg geen tijdvak om te tonen (AC-1). */}
                {regel.startTime && regel.endTime && (
                  <span className="text-gray-500">
                    {' '}
                    {regel.startTime}–{regel.endTime}
                  </span>
                )}
                <span className="text-gray-500"> · {urenTekst(regel.hours)}</span>
                <span className="text-gray-500"> · {regel.leaveTypeName}</span>
                {gedeeldeTermijn === null && (
                  <p className="mt-0.5 text-xs text-gray-400">
                    {slotregelTekst(regel.correctieTermijnEinde, false, peildatum)}
                  </p>
                )}
                {fout && (
                  <p id={foutId} className="mt-0.5 text-xs text-[#1E293B]">
                    {fout}
                  </p>
                )}
              </div>

              {isBevestigd ? (
                <p className="text-xs text-gray-500">{BEVESTIGD_TEKST}</p>
              ) : (
                <div className="flex items-center gap-2">
                  {/* AC-6: ligt er een correctie in behandeling, dan valt er
                      niets te bevestigen — bevestigen en corrigeren sluiten
                      elkaar uit. De weg naar de correctie blijft wél open. */}
                  {!regel.hasPendingCorrection && (
                    <button
                      type="button"
                      data-testid="uren-bevestig-knop"
                      onClick={() => onBevestig(regel.id)}
                      aria-describedby={foutId}
                      className={PRIMAIRE_KNOP}
                    >
                      Ja, dat klopt
                    </button>
                  )}
                  <button
                    type="button"
                    onClick={() => onAanpassen(regel.id)}
                    className={SECUNDAIRE_KNOP}
                  >
                    Nee, aanpassen
                  </button>
                </div>
              )}
            </li>
          );
        })}
      </ul>

      {gedeeldeTermijn !== null && (
        <p className="mt-3 text-xs text-gray-400">
          {slotregelTekst(gedeeldeTermijn, false, peildatum)}
        </p>
      )}
    </section>
  );
}
